'use client'

import React, { useEffect, useState } from 'react'
import { clientLoginHref } from '@/domains/auth/utils/sign-out'
import styles from './feedback.module.css'

export type AuthPendingPhase = 'restore' | 'signin' | 'redirect' | 'verify'

export interface AuthPendingScreenProps {
  /** Explicit message — overrides phase defaults when provided. */
  message?: string
  /** Default copy selector when message is omitted. */
  phase?: AuthPendingPhase
  className?: string
  /** Full auth canvas or an in-shell workspace state. */
  variant?: 'fullscreen' | 'workspace'
  /** Accessible label (defaults to message) */
  'aria-label'?: string
}

const PHASE_MESSAGES: Record<AuthPendingPhase, string> = {
  restore: 'Checking your session…',
  signin: 'Signing in…',
  redirect: 'Redirecting…',
  verify: 'Validating your access…',
}

/** FinTech desk strip codes — mono, scannable, not marketing. */
const PHASE_META: Record<
  AuthPendingPhase,
  { code: string; status: string; hint: string }
> = {
  restore: {
    code: 'RESTORE',
    status: 'CHECKING',
    hint: 'Verifying cookies and session integrity — not an empty workspace.',
  },
  signin: {
    code: 'AUTH',
    status: 'SIGNING',
    hint: 'Completing credential exchange. Desk tools unlock after this step.',
  },
  redirect: {
    code: 'ROUTE',
    status: 'REDIRECT',
    hint: 'Moving you to the correct surface. Hold — do not refresh.',
  },
  verify: {
    code: 'ACCESS',
    status: 'VERIFY',
    hint: 'Confirming role and policy gates before the control plane opens.',
  },
}

function formatDeskClock(d: Date): string {
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  const ss = String(d.getSeconds()).padStart(2, '0')
  return `${hh}:${mm}:${ss}`
}

/**
 * Auth-tier pending surface — fintech-trading-ux + ui-ux-pro-max 2-tier model.
 *
 * Shown only while session restore / cookie verify / OIDC is in progress.
 * Never used for authenticated app bootstrapping (use AppLoadingScreen).
 *
 * Desk honesty: mono phase/status/clock strip, elevated panel, no blank sand void.
 */
/** Survives parent remounts so a flickering isLoading cannot reset the 8s cap. */
let restoreShownAt = 0

export function resetAuthPendingRestoreClock(): void {
  restoreShownAt = 0
}

function currentReturnTo(): string {
  if (typeof window === 'undefined') return '/client/dashboard'
  return `${window.location.pathname}${window.location.search || ''}`
}

export function AuthPendingScreen({
  message,
  phase = 'restore',
  className = '',
  variant = 'fullscreen',
  'aria-label': ariaLabel,
}: AuthPendingScreenProps) {
  const displayMessage = message ?? PHASE_MESSAGES[phase]
  const meta = PHASE_META[phase]
  const label = ariaLabel || displayMessage
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000)
    return () => window.clearInterval(id)
  }, [])

  if (restoreShownAt === 0) {
    restoreShownAt = Date.now()
  }
  const [retryNonce, setRetryNonce] = useState(0)
  const [stuck, setStuck] = useState(
    () => Date.now() - restoreShownAt >= 8_000
  )
  useEffect(() => {
    const remain = Math.max(0, 8_000 - (Date.now() - restoreShownAt))
    const id = window.setTimeout(() => setStuck(true), remain)
    return () => window.clearTimeout(id)
  }, [retryNonce])

  const retryRestore = () => {
    restoreShownAt = Date.now()
    setStuck(false)
    setRetryNonce((n) => n + 1)
    window.dispatchEvent(new Event('mechagora:auth-pending-retry'))
  }

  const goToSignIn = () => {
    window.location.assign(
      clientLoginHref({
        error: 'session_expired',
        returnTo: currentReturnTo(),
      })
    )
  }

  const openDesk = () => {
    try {
      sessionStorage.setItem('mechagora:skip-auth-pending', '1')
    } catch {
      /* no-op */
    }
    retryRestore()
  }

  useEffect(() => {
    if (!stuck) return
    window.dispatchEvent(new Event('mechagora:auth-pending-timeout'))
  }, [stuck])

  const rootClass = [
    styles.authPending,
    variant === 'workspace'
      ? styles.authPendingWorkspace
      : styles.authPendingFullscreen,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      className={rootClass}
      role={stuck ? "alert" : "status"}
      aria-live="polite"
      aria-busy={stuck ? undefined : true}
      aria-label={label}
      data-phase={phase}
      data-variant={variant}
    >
      {variant === 'fullscreen' ? (
        <div className={styles.authPendingAmbient} aria-hidden />
      ) : null}

      <div className={styles.authPendingPanel}>
        <header className={styles.authPendingHeader}>
          <div className={styles.authPendingBrand}>
            <span className={styles.authPendingMark} aria-hidden>
              M
            </span>
            <div className={styles.authPendingBrandCopy}>
              <span className={styles.authPendingBrandName}>Mechagora</span>
              <span className={styles.authPendingBrandMeta}>SESSION · DESK</span>
            </div>
          </div>
          <span
            className={styles.authPendingLive}
            data-state={phase}
            title="Session handshake in progress — not a live market feed"
          >
            <span className={styles.authPendingLiveDot} aria-hidden />
            WAIT
          </span>
        </header>

        <div className={styles.authPendingStrip} aria-hidden>
          <span className={styles.authPendingLabel}>PHASE</span>
          <span className={styles.authPendingValue} data-state={phase}>
            {meta.code}
          </span>
          <span className={styles.authPendingLabel}>STATUS</span>
          <span className={styles.authPendingValue} data-state={phase}>
            {meta.status}
          </span>
          <span className={styles.authPendingLabel}>CLOCK</span>
          <time
            className={styles.authPendingValue}
            dateTime={now.toISOString()}
          >
            {formatDeskClock(now)}
          </time>
        </div>

        <div className={styles.authPendingBody}>
          <div className={styles.authPendingActivity} aria-hidden>
            <span className={styles.authPendingBar} />
            <span className={styles.authPendingBar} />
            <span className={styles.authPendingBar} />
            <span className={styles.authPendingBar} />
            <span className={styles.authPendingBar} />
          </div>
          <p className={styles.authPendingMessage}>{displayMessage}</p>
          <p className={styles.authPendingHint}>
            {stuck
              ? 'Session restore timed out. Agent controls stay locked until this completes.'
              : meta.hint}
          </p>
          {stuck ? (
            <p className={styles.authPendingHint}>
              <button type="button" onClick={retryRestore}>
                Try again
              </button>{' '}
              <button type="button" onClick={goToSignIn}>
                Sign in
              </button>{' '}
              <button type="button" onClick={openDesk}>
                Open desk
              </button>
            </p>
          ) : null}
        </div>

        <footer className={styles.authPendingFooter} aria-hidden>
          <span>AUTH TIER</span>
          <span className={styles.authPendingFooterSep}>·</span>
          <span>NO MARKET ACTIONS</span>
        </footer>
      </div>
    </div>
  )
}

export default AuthPendingScreen
